import React, { useState } from 'react';
import { BarChart3 } from 'lucide-react';

const DetailedChart = ({ data, metricKey, chartType, color }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Get campaign data sorted by date
  const getDetailedChartData = () => {
    if (data.length === 0) {
      return { values: [], campaigns: [], labels: [] };
    }

    const recentCampaigns = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

    const values = recentCampaigns.map(campaign => {
      switch(metricKey) {
        case 'invitations': return campaign.invitations || 0;
        case 'registrations': return campaign.registrations.total || 0;
        case 'attendees': return campaign.attendees.total || 0;
        case 'bookings': return campaign.bookings.total || 0;
        case 'show': return campaign.sales.show || 0;
        case 'sales': return campaign.sales.total || 0;
        case 'inviteToReg': return parseFloat(campaign.conversions.inviteToReg) || 0;
        case 'regToAttend': return parseFloat(campaign.conversions.regToAttend) || 0;
        case 'attendToBook': return parseFloat(campaign.conversions.attendToBook) || 0;
        case 'bookedToShow': return parseFloat(campaign.conversions.bookedToShow) || 0;
        case 'showToSale': return parseFloat(campaign.conversions.showToSale) || 0;
        case 'revenue': return campaign.sales.revenue|| 0;
        default: return 0;
      }
    });

    const labels = recentCampaigns.map(campaign => `#${campaign.id}`);

    // Calculate percentage changes
    const percentageChanges = values.map((value, index) => {
      if (index === 0) return 0;
      const previousValue = values[index - 1];
      if (previousValue === 0) return value > 0 ? 100 : 0;
      return ((value - previousValue) / previousValue) * 100;
    });

    return { values, campaigns: recentCampaigns, labels, percentageChanges };
  };

  const { values: chartData, campaigns: chartCampaigns, labels, percentageChanges } = getDetailedChartData();
  
  // ✅ FIX 2: Calculate Y-axis range with padding
  const dataMax = Math.max(...chartData.filter(v => v > 0)) || 1;
  const dataMin = Math.min(...chartData.filter(v => v >= 0)) || 0;
  
  // Add 10% padding to top and bottom for better visibility
  const padding = (dataMax - dataMin) * 0.1;
  const maxValue = dataMax + padding;
  const minValue = Math.max(0, dataMin - padding); // Don't go below 0 unless data is negative
  
  const hasData = chartData.some(v => v > 0);

  // Calculate dynamic height
  const calculateHeight = () => {
    const baseHeight = 300;
    const maxHeight = 500;
    
    if (chartType === "bar") {
      const dynamicHeight = Math.max(baseHeight, Math.min(maxHeight, baseHeight + (chartData.length * 5)));
      return dynamicHeight;
    } else {
      return Math.min(maxHeight, baseHeight + 100);
    }
  };

  const chartHeight = calculateHeight();

  const colorMap = {
    darkblue:    { main: '#152451', light: '#e3e8f1', dark: '#0e1a34' },
    violet:      { main: '#92278f', light: '#f5eaf5', dark: '#681c64' },
    teal:        { main: '#009391', light: '#d0fffc', dark: '#005f5e' },
    brand:       { main: '#f58025', light: '#fdead6', dark: '#bf4d0f' },
    brightblue:  { main: '#0093cf', light: '#e0f2fe', dark: '#00608a' },
    pink:        { main: '#ed145b', light: '#fee5f0', dark: '#af0d42' },
    green:       { main: '#b0bc22', light: '#f1f8cd', dark: '#687116' },
    yellow:      { main: '#ffdd00', light: '#fffcc1', dark: '#d1a800' },
    primary:     { main: '#f58025', light: '#fdead6', dark: '#bf4d0f' },
    secondary:   { main: '#152451', light: '#e3e8f1', dark: '#0e1a34' },
    accent:      { main: '#92278f', light: '#f5eaf5', dark: '#681c64' },
    purple:      { main: '#92278f', light: '#f5eaf5', dark: '#681c64' },
    blue:        { main: '#0093cf', light: '#e0f2fe', dark: '#00608a' },
    orange:      { main: '#f58025', light: '#fdead6', dark: '#bf4d0f' },
  };
  const colors = colorMap[color] || colorMap.blue;

  const formatValue = (value) => {
    if (metricKey.includes('To') || metricKey.includes('Percent')) {
      return `${value.toFixed(1)}%`;
    } else if (metricKey === 'revenue') {
      return `$${value.toLocaleString()}`;
    } else {
      return value.toLocaleString();
    }
  };

  const formatPercentageChange = (change) => {
    if (change === 0) return "No change";
    const sign = change > 0 ? "+" : "";
    return `${sign}${change.toFixed(1)}%`;
  };

  // Get breakdown data for tooltip
  const getBreakdownData = (campaign) => {
    switch(metricKey) {
      case 'invitations':
        return [
          { label: 'Cold Calls', value: campaign.calls.cold },
          { label: 'Expansion', value: campaign.calls.expansion }
        ];
      case 'registrations':
        return [
          { label: 'Cold Calls Yes', value: campaign.registrations.coldCalls },
          { label: 'Maybe', value: campaign.registrations.maybe },
          { label: 'LinkedIn', value: campaign.registrations.linkedin },
          { label: 'Webinar Tool', value: campaign.registrations.zoom }
        ];
      case 'attendees':
        return [
          { label: 'Cold Calls', value: campaign.attendees.coldCalls },
          { label: 'LinkedIn', value: campaign.attendees.linkedin },
          { label: 'Other Sources', value: campaign.attendees.other },
          { label: 'Unique Viewers', value: campaign.attendees.uniqueViewers }
        ];
      case 'bookings':
        return [
          { label: 'Webinar', value: campaign.bookings.webinar },
          { label: 'Website', value: campaign.bookings.site },
          { label: 'Email/SMS', value: campaign.bookings.email },
          { label: 'Other', value: campaign.bookings.other }
        ];
      case 'show':
        return [
          { label: 'Showed', value: campaign.sales.show },
          { label: 'No Show', value: campaign.sales.noShow }
        ];
      case 'sales':
        return [
          { label: 'Qualified', value: campaign.sales.qualified },
          { label: 'Sales', value: campaign.sales.total }
        ];
      default:
        return [];
    }
  };

  if (!hasData) {
    return (
      <div className="h-80 flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center">
          <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No data available for the last 90 days</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ height: `${chartHeight}px` }}>
      {chartType === "bar" ? (
        <div className="h-full flex flex-col relative" style={{ paddingBottom: '60px' }}>
          <div className="flex-1 pb-4">
            <div className="flex items-end gap-2 px-4 h-full w-full"
                style={{
                  height: chartHeight - 80
                }}>
              {chartData.map((value, i) => (
                <div key={i} className="flex flex-col items-center relative flex-1 min-w-0">
                  <div
                    className="w-full rounded-t-lg transition-all duration-300 hover:opacity-80 cursor-pointer relative group"
                    style={{
                      backgroundColor: colors.main,
                      height: `${Math.max((value / maxValue) * (chartHeight - 140), value > 0 ? 8 : 0)}px`,
                      minHeight: value > 0 ? '8px' : '0px'
                    }}
                    onMouseEnter={() => setHoveredPoint(i)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                  <div className="text-[10px] text-gray-600 mt-1 font-medium text-center truncate w-full" style={{ writingMode: chartData.length > 12 ? 'vertical-rl' : 'horizontal-tb', transform: chartData.length > 12 ? 'rotate(180deg)' : 'none', maxHeight: chartData.length > 12 ? '50px' : 'none' }}>
                    #{chartCampaigns[i]?.id}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Enhanced Hover Tooltip for Bar Chart */}
          {hoveredPoint !== null && (
            <div className="fixed bg-gray-900 text-white p-4 rounded-lg shadow-xl z-50 pointer-events-none min-w-64 max-w-80"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)'
                }}>
              <div className="text-sm font-semibold text-blue-300">Webinar #{chartCampaigns[hoveredPoint]?.id}</div>
              <div className="text-xs text-gray-400 mb-3">
                {new Date(chartCampaigns[hoveredPoint]?.date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              
              <div className="text-xl font-bold text-white mb-3">{formatValue(chartData[hoveredPoint])}</div>
              
              {hoveredPoint > 0 && (
                <div className={`text-sm mb-3 ${percentageChanges[hoveredPoint] >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {percentageChanges[hoveredPoint] >= 0 ? '↗' : '↘'} {formatPercentageChange(percentageChanges[hoveredPoint])} vs previous
                </div>
              )}

              {/* Breakdown Data */}
              <div className="border-t border-gray-700 pt-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Breakdown:</div>
                <div className="max-h-32 overflow-y-auto">
                  {getBreakdownData(chartCampaigns[hoveredPoint]).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs mb-1">
                      <span className="text-gray-300">{item.label}:</span>
                      <span className="text-white font-medium">{item.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-8 border-transparent border-t-gray-900"></div>
            </div>
          )}
        </div>
      ) : (
        // ✅ FIX 1: Added extra padding and overflow visible for line chart
        <div className="h-full relative overflow-visible" style={{ 
          paddingBottom: '80px', 
          paddingRight: '50px',  // ✅ Extra right padding for last point hover
          paddingLeft: '20px',   // ✅ Extra left padding
          paddingTop: '20px'     // ✅ Extra top padding
        }}>
          <svg 
            className="w-full h-full overflow-visible" 
            viewBox={`0 0 800 ${chartHeight - 40}`} 
            preserveAspectRatio="none"
            style={{ overflow: 'visible' }} // ✅ Allow SVG overflow for tooltips
          >
            <defs>
              <linearGradient id={`detailed-gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={colors.main} stopOpacity="0.3"/>
                <stop offset="100%" stopColor={colors.main} stopOpacity="0.05"/>
              </linearGradient>
            </defs>
            
            {/* ✅ FIX 1: Adjusted chart area margins for better spacing */}
            {/* Grid lines */}
            <g stroke="#e5e7eb" strokeWidth="1" opacity="0.3">
              {[0, 1, 2, 3, 4].map(i => (
                <line key={i} x1="80" y1={60 + i * ((chartHeight - 200) / 4)} x2="720" y2={60 + i * ((chartHeight - 200) / 4)} />
              ))}
            </g>

            {/* Area fill */}
            <path
              d={`M 80,${chartHeight - 140} ${chartData.map((value, i) => 
                `L ${80 + (i / (chartData.length - 1)) * 640},${chartHeight - 140 - ((value - minValue) / (maxValue - minValue)) * (chartHeight - 240)}`
              ).join(' ')} L ${80 + 640},${chartHeight - 140} Z`}
              fill={`url(#detailed-gradient-${color})`}
            />
            
            {/* Line */}
            <polyline
              fill="none"
              stroke={colors.main}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={chartData.map((value, i) => 
                `${80 + (i / (chartData.length - 1)) * 640},${chartHeight - 140 - ((value - minValue) / (maxValue - minValue)) * (chartHeight - 240)}`
              ).join(' ')}
            />
            
            {/* ✅ FIX 1: Enhanced data points with larger hover areas */}
            {chartData.map((value, i) => (
              <g key={i}>
                {/* Invisible larger hit area for easier hovering */}
                <circle
                  cx={80 + (i / (chartData.length - 1)) * 640}
                  cy={chartHeight - 140 - ((value - minValue) / (maxValue - minValue)) * (chartHeight - 240)}
                  r="15" // ✅ Large invisible hover area
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredPoint(i)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
                {/* Visible dot */}
                <circle
                  cx={80 + (i / (chartData.length - 1)) * 640}
                  cy={chartHeight - 140 - ((value - minValue) / (maxValue - minValue)) * (chartHeight - 240)}
                  r="6"
                  fill={colors.dark}
                  className="opacity-80 hover:opacity-100 pointer-events-none transition-all duration-200"
                  style={{ filter: hoveredPoint === i ? 'drop-shadow(0 0 6px rgba(0,0,0,0.3))' : 'none' }}
                />
              </g>
            ))}

            {/* ✅ FIX 2: Updated Y-axis labels with new range */}
            <g className="text-xs fill-gray-500">
              {[0, 1, 2, 3, 4].map(i => {
                const labelValue = minValue + ((maxValue - minValue) / 4) * (4 - i);
                return (
                  <text key={i} x="70" y={65 + i * ((chartHeight - 200) / 4)} textAnchor="end" dominantBaseline="middle">
                    {formatValue(labelValue)}
                  </text>
                );
              })}
            </g>

            {/* X-axis labels */}
            <g className="text-xs fill-gray-500">
              {labels.map((label, i) => (
                <text 
                  key={i} 
                  x={80 + (i / (chartData.length - 1)) * 640} 
                  y={chartHeight - 100} 
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {label}
                </text>
              ))}
            </g>
          </svg>
          
          {/* ✅ FIX 1: Enhanced positioning for tooltip to prevent edge clipping */}
          {hoveredPoint !== null && (
            <div className="absolute bg-gray-900 text-white p-4 rounded-lg shadow-xl z-20 pointer-events-none min-w-64"
                style={{
                  // ✅ Better positioning logic that keeps tooltip in view
                  left: `${Math.min(
                    Math.max(80 + (hoveredPoint / (chartData.length - 1)) * 640, 130), // Min 130px from left
                    700 // Max 700px from left (leaves room for tooltip width)
                  )}px`,
                  top: `${Math.max(
                    chartHeight - 140 - ((chartData[hoveredPoint] - minValue) / (maxValue - minValue)) * (chartHeight - 240) - 160, 
                    20 // Min 20px from top
                  )}px`,
                  transform: hoveredPoint === chartData.length - 1 ? 'translateX(-100%)' : 
                            hoveredPoint === 0 ? 'translateX(0%)' : 'translateX(-50%)'
                }}>
              <div className="text-sm font-semibold text-blue-300">Webinar #{chartCampaigns[hoveredPoint]?.id}</div>
              <div className="text-xs text-gray-400 mb-3">
                {new Date(chartCampaigns[hoveredPoint]?.date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              
              <div className="text-xl font-bold text-white mb-3">{formatValue(chartData[hoveredPoint])}</div>
              
              {hoveredPoint > 0 && (
                <div className={`text-sm mb-3 ${percentageChanges[hoveredPoint] >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {percentageChanges[hoveredPoint] >= 0 ? '↗' : '↘'} {formatPercentageChange(percentageChanges[hoveredPoint])} vs previous
                </div>
              )}

              {/* Breakdown Data */}
              <div className="border-t border-gray-700 pt-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Breakdown:</div>
                {getBreakdownData(chartCampaigns[hoveredPoint]).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs mb-1">
                    <span className="text-gray-300">{item.label}:</span>
                    <span className="text-white font-medium">{item.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-6 border-transparent border-t-gray-900"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailedChart;